// Registers (tills) at one location — ops simulation T12. An owner adds
// 'Till 1', 'Till 2'…; each POS device picks its register once. A register
// with an open session cannot be switched off.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, useToast } from '@bbpos/ui';
import { registerService } from '@/services/register.service';
import type { Register } from '@/services/register.service';

export function LocationRegistersCard({ locationId, canEdit }: { locationId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);

  const key = ['registers', locationId];
  const { data: registers = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: () => registerService.list(locationId),
  });

  const onError = (err: Error) => toast({ title: err.message ?? 'Could not save the register', variant: 'error' });
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });

  const create = useMutation({
    mutationFn: () => registerService.create(locationId, name.trim()),
    onSuccess: () => { setName(''); refresh(); toast({ title: 'Register added', variant: 'success' }); },
    onError,
  });
  const update = useMutation({
    mutationFn: (v: { id: string; body: { name?: string; is_active?: boolean } }) => registerService.update(v.id, v.body),
    onSuccess: () => { setEditing(null); refresh(); },
    onError,
  });

  return (
    <div className="rounded-2xl bg-white/80 dark:bg-neutral-900/80 shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] p-6 mt-6 animate-slide-up">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-1">
        Registers
      </h3>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
        Each till picks its register once. Bills record the register, the session and the cashier.
      </p>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading…</p>
      ) : registers.length === 0 ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
          No registers yet. Until you add one, bills at this store are not tied to a register.
        </p>
      ) : (
        <ul className="divide-y divide-neutral-100 dark:divide-neutral-800 mb-4">
          {registers.map((r: Register) => (
            <li key={r.id} className="flex items-center gap-3 py-2.5">
              {editing?.id === r.id ? (
                <>
                  <div className="flex-1">
                    <Input
                      id={`register-name-${r.id}`}
                      value={editing.name}
                      onChange={(v) => setEditing({ id: r.id, name: v })}
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={!editing.name.trim()}
                    loading={update.isPending}
                    onClick={() => update.mutate({ id: r.id, body: { name: editing.name.trim() } })}
                  >
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                </>
              ) : (
                <>
                  <span className={`flex-1 text-sm font-medium ${r.is_active ? 'text-neutral-900 dark:text-white' : 'text-neutral-400 line-through'}`}>
                    {r.name}
                  </span>
                  <span
                    className={
                      r.open_session_id
                        ? 'rounded-full bg-success-100 dark:bg-success-900/40 px-2 py-0.5 text-xs font-semibold text-success-700 dark:text-success-300'
                        : 'rounded-full bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 text-xs font-semibold text-neutral-500'
                    }
                  >
                    {r.open_session_id ? `Open · ${r.open_session_number}` : r.is_active ? 'Closed' : 'Off'}
                  </span>
                  {canEdit && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ id: r.id, name: r.name })}>Rename</Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={r.is_active && !!r.open_session_id}
                        onClick={() => update.mutate({ id: r.id, body: { is_active: !r.is_active } })}
                      >
                        {r.is_active ? 'Switch off' : 'Switch on'}
                      </Button>
                    </>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Input id="new-register-name" placeholder="e.g. Till 1" value={name} onChange={setName} />
          </div>
          <Button variant="primary" disabled={!name.trim()} loading={create.isPending} onClick={() => create.mutate()}>
            Add register
          </Button>
        </div>
      )}
    </div>
  );
}
